import {
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  endOfWeek,
  fromUnixTime,
  getUnixTime,
  secondsToMinutes,
  setHours,
  setMinutes,
  startOfDay,
  subHours,
  subMinutes,
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { DateTime } from 'timezonecomplete';

import type {
  IContractRequirementHour,
  IOffsetData,
  IParsedItem,
  IScheduleAvailabilityItem,
} from '@/components/agent/my-availability/types';
import type {
  IAgentRaw,
  IBoundaryBlocks,
  IContractRaw,
  IGetAdherenceReason,
  IGetContractAdherence,
  IGetContractAttendance,
  IGetContractAttendanceResponse,
  IResignSurveyRaw,
  IUnmetContractRequirementRaw,
} from '@/api/types';
import type {
  IAgent,
  IAgentsCategorized,
  IContractRequirementItem,
  IResignationPending,
  IResignContractStepTwo,
  IUnmetContractRequirement,
} from '@/store/types';
import type { IGigComplianceRow, IRequirementValue, IValueWarningPair } from '@/components/agent/gig-compliance/types';
import type { IClientSummary } from '@/store/admin/types/types';
import type { ScheduleId } from '@/types/schedule';
import type { IWeek, IWorkSchedule } from '@/types/calendar';

import i18n from '@/config/i18n';
import {
  convertMinutesFromMidnightToPX,
  convertWeekdayIndexIntoName,
  convertWeekdayIndexIntoShortName,
  formatDate,
  getBlockFromDate,
  getDateFromWeekId,
  getTimestampFromDateString,
  getWorkSchedule,
  makeArraySet,
} from '@/helpers';
import { HOUR_IN_PX, MINUTES_IN_HOUR, MONTH_DAY_FORMAT, MONTH_FULL_DAY_YEAR_FORMAT } from '@/config/variables';
import { ContractRequirementStatus, ContractStatus, EffectiveType, RequirementType, ResTermType } from '@/store/enums';
import { baseCategorizedAgents } from '@/constants/client';

const initialParsedItem = {
  colSpan: 1,
  colOffset: 0,
  toBeDeleted: false,
  isModified: false,
  isNew: false,
  resizeData: {
    handle: 'e',
    size: {
      width: 0,
      height: 0,
    },
    leftOffset: undefined,
    topOffset: 0,
    resizeOffset: 0,
    maxConstraints: [Infinity, Infinity] as [number, number],
    initialSize: {
      width: 0,
      height: 0,
    },
  },
} as IParsedItem;

const calculateUnmetBlocks = (unmetRequirement?: IUnmetContractRequirementRaw): { now: number; max: number } => {
  if (!unmetRequirement) {
    return { now: 0, max: 0 };
  }

  return {
    now: (unmetRequirement.weeklyBlocks + unmetRequirement.unmetBlocks) / 2,
    max: unmetRequirement.weeklyBlocks / 2,
  };
};

const calculateContractState = (unmetRequirement?: IUnmetContractRequirementRaw): ContractRequirementStatus => {
  if (!unmetRequirement) return ContractRequirementStatus.Danger;

  const { weeklyBlocks, unmetBlocks } = unmetRequirement;

  if (weeklyBlocks + unmetBlocks !== weeklyBlocks) {
    return ContractRequirementStatus.Danger;
  } else {
    return ContractRequirementStatus.Success;
  }
};

const getWorkSchedules = (
  contractRequirementBlocks: {
    startOffset: number;
    endOffset: number;
  }[],
  timeZone: string,
  clientTimezone?: string,
  displayTimezone?: boolean,
): IWorkSchedule[] =>
  contractRequirementBlocks.map(block => getWorkSchedule(block, timeZone, clientTimezone, displayTimezone));

function getHourFromOffsetAndWeekId(
  weekId: number,
  offset: number,
  timeZone: string,
  clientTimezone?: string,
): IContractRequirementHour {
  const d = getDateFromWeekId(weekId, _getWeekdayIndexFromOffset(offset));
  const dateWithProperHour = setHours(d, _getHourFromOffset(offset));
  const dateWithProperMinute = setMinutes(dateWithProperHour, _getMinuteFromOffset(offset) * (MINUTES_IN_HOUR / 2));
  dateWithProperMinute.setSeconds(0);
  const dateUTC = fromZonedTime(dateWithProperMinute, clientTimezone ?? timeZone);
  const zonedDate = toZonedTime(dateUTC, timeZone);

  return {
    hours: zonedDate.getHours(),
    minutes: zonedDate.getMinutes(),
    weekdayIndex: _getWeekdayIndexFromOffset(offset),
    unix: getUnixTime(zonedDate),
  };
}

const getWeekdayFromOffsetAndWeekId = (offset: number): string =>
  convertWeekdayIndexIntoName(_getWeekdayIndexFromOffset(offset));
const getWeekdayShortNameFromOffsetAndWeekId = (offset: number): string =>
  convertWeekdayIndexIntoShortName(_getWeekdayIndexFromOffset(offset));
const _getHourFromOffset = (offset: number) => Math.floor((offset % 48) / 2);
const _getMinuteFromOffset = (offset: number) => (offset % 48) % 2; // 0 == 00min, 1 == 30min

const _getWeekdayIndexFromOffset = (offset: number) => Math.floor(offset / 48);
const _getFirstBlockForWeekday = (weekdayIndex: number) => weekdayIndex * 48;
const _getLastBlockForWeekday = (weekdayIndex: number) => _getFirstBlockForWeekday(weekdayIndex) + 48 - 1;

const getOffsetData = (
  startOffset: number,
  endOffset: number,
  timeZone: string,
  clientTimezone?: string,
  weekday?: string,
): IOffsetData => {
  const start: IContractRequirementHour = getHourFromOffsetAndWeekId(
    Number(new URLSearchParams(location.search).get('weekid')),
    startOffset,
    timeZone,
    clientTimezone,
  );
  const end: IContractRequirementHour = getHourFromOffsetAndWeekId(
    Number(new URLSearchParams(location.search).get('weekid')),
    endOffset + 1,
    timeZone,
    clientTimezone,
  );

  const d = new Date();
  d.setHours(start.hours);
  d.setMinutes(start.minutes);
  const blockDuration =
    Math.abs(differenceInMinutes(fromUnixTime(start.unix), fromUnixTime(end.unix))) / MINUTES_IN_HOUR;

  return {
    weekday: weekday ?? getWeekdayFromOffsetAndWeekId(startOffset),
    startHour: start,
    endHour: end,
    minutesFromMidnight: convertMinutesFromMidnightToPX(d),
    blockDuration: blockDuration * HOUR_IN_PX,
  };
};

const isBlockCoveredByDst = (week: IWeek, block: IBoundaryBlocks): boolean => {
  if (!week.dst) return false;

  const { startBlock, endBlock } = block;
  const { startBlock: dlsStartBlock, endBlock: dlsEndBlock } = week.dst;

  return startBlock >= dlsStartBlock && endBlock <= dlsEndBlock;
};

const getTimezoneBlocksDifference = (tz1: string, tz2?: string) =>
  differenceInHours(toZonedTime(Date.now(), tz1), tz2 ? toZonedTime(Date.now(), tz2) : Date.now()) * 2;

const getOffsetDataArray = (
  startOffset: number,
  endOffset: number,
  timeZone: string,
  clientTimezone?: string,
): IOffsetData[] => {
  const startWeekday = getWeekdayFromOffsetAndWeekId(startOffset);
  const endWeekday = getWeekdayFromOffsetAndWeekId(endOffset + 1);
  const isTheSameDay = startWeekday === endWeekday;

  if (isTheSameDay) return [getOffsetData(startOffset, endOffset, timeZone, clientTimezone)];

  const startWeekdayEndHourOffset = _getLastBlockForWeekday(_getWeekdayIndexFromOffset(startOffset));
  const endWeekdayStartHourOffset = _getFirstBlockForWeekday(_getWeekdayIndexFromOffset(endOffset + 1));

  const tzDiff = getTimezoneBlocksDifference(timeZone, clientTimezone);
  return [
    getOffsetData(startOffset, startWeekdayEndHourOffset - tzDiff, timeZone, clientTimezone, startWeekday),
    getOffsetData(endWeekdayStartHourOffset - tzDiff, endOffset, timeZone, clientTimezone, endWeekday),
  ];
};

const getAvailabilityItems = (
  arr: (IContractRequirementItem & IClientSummary)[],
  scheduleId: ScheduleId,
  timeZone: string,
  dayName: string,
): IScheduleAvailabilityItem[] => {
  return arr
    .filter(requirement => requirement.contractRequirementScheduleSummary.some(item => item.scheduleId === scheduleId))
    .flatMap(requirement => {
      return requirement.contractRequirementBlocks.map(item => ({
        ...item,
        clientTimezone: requirement.clientTimezone,
      }));
    })
    .flatMap(item =>
      getOffsetDataArray(item.startOffset, item.endOffset, timeZone, item.clientTimezone).map(d => ({
        ...d,
        contractRequirementId: item.contractRequirementId,
      })),
    )
    .filter(item => item.weekday.slice(0, 3) === dayName);
};

const prepareGigComplianceToDisplay = (
  weekIds: number[],
  contractAttendance: IGetContractAttendance[],
  contractAdherence: IGetContractAdherence[],
  contractAdherenceReason: IGetAdherenceReason[],
): IGigComplianceRow[] =>
  weekIds.map(weekId => {
    const maybeAttendance = contractAttendance.find(attendance => attendance.weekId === weekId);
    const maybeAdherence = contractAdherence.filter(adherence => adherence.weekId === weekId);

    const reqMinRaw = maybeAdherence.filter(adherence => adherence.typeName === RequirementType.min);
    const reqMaxRaw = maybeAdherence.filter(adherence => adherence.typeName === RequirementType.max);
    const reqBaseRaw = maybeAdherence.find(adherence => adherence.typeName === RequirementType.base);
    const reqPeakRaw = maybeAdherence.find(adherence => adherence.typeName === RequirementType.peak);
    const weekSpan = `
      ${formatDate(getDateFromWeekId(weekId), MONTH_DAY_FORMAT)}
      -
      ${formatDate(endOfWeek(getDateFromWeekId(weekId), { weekStartsOn: 1 }), MONTH_FULL_DAY_YEAR_FORMAT)}
    `;

    const { reasonTxt, reasonNote } = _getReasonByWeekId(contractAdherenceReason, weekId);

    const reqMin = reqMinRaw?.map(req => _validateContractRequirements(req));
    const reqMax = reqMaxRaw?.map(req => _validateContractRequirements(req));

    const result = <IGigComplianceRow>{
      weekId,
      weekSpan,
      reqMin: makeArraySet(
        reqMin.length
          ? reqMin
          : <IRequirementValue[]>[{ requirementId: 0, gigValue: '-', isWarningGig: false, isWarningNetwork: false }],
        'requirementId',
      ),
      reqMax: makeArraySet(reqMax, 'requirementId'),
      reqBase: _validateContractRequirements(reqBaseRaw),
      reqPeak: _validateContractRequirements(reqPeakRaw),
      noShows: _validateComplianceCells(maybeAttendance?.noShow),
      lateLogin10Minus: _validateComplianceCells(maybeAttendance?.lateLogin5To10),
      lateLogin10Plus: _validateComplianceCells(maybeAttendance?.lateLogin10Plus),
      lateAvg: _validateComplianceCells(maybeAttendance?.lateLoginAvgSeconds),
      earlyLogout10Minus: _validateComplianceCells(maybeAttendance?.earlyLogout5To10),
      earlyLogout10Plus: _validateComplianceCells(maybeAttendance?.earlyLogout10Plus),
      earlyAvg: _validateComplianceCells(maybeAttendance?.earlyLogoutAvgSeconds),
      reason: { reasonTxt, reasonNote, isRequired: false, isWarning: false },
    };

    result.reason.isRequired = _checkIfReasonRequired(result);
    // TODO: when the reason field is required, but not isWarning?
    result.reason.isWarning = result.reason.isRequired;
    const incomplete =
      hasIncompleteValue(result.earlyAvg) ||
      hasIncompleteValue(result.earlyLogout10Minus) ||
      hasIncompleteValue(result.earlyLogout10Plus) ||
      hasIncompleteValue(result.lateAvg) ||
      hasIncompleteValue(result.lateLogin10Minus) ||
      hasIncompleteValue(result.lateLogin10Plus) ||
      hasIncompleteValue(result.noShows);
    result.isPublished =
      incomplete ||
      [...result.reqMin, ...result.reqMax, result.reqPeak, result.reqBase].some(item => !!item?.isValuePublished);
    return result;
  });

const hasIncompleteValue = (valueWarningPair: IValueWarningPair | undefined): boolean => {
  if (
    !valueWarningPair ||
    !valueWarningPair?.value ||
    valueWarningPair?.value === '-' ||
    valueWarningPair.value === '0'
  )
    return false;
  const valueNumber = Number(valueWarningPair.value);
  return !isNaN(valueNumber) && valueNumber > 0;
};

const _validateContractRequirements = (requirement: IGetContractAdherence | undefined): IRequirementValue => {
  const blocks = requirement?.weeklyBlocks;
  const unmet = requirement?.unmetBlocks ?? 0;
  const networkBlocks = requirement?.actualBlocks ?? 0;
  const requirementId = requirement?.contractRequirementId ?? 0;

  if (!blocks || !requirement)
    return {
      contractId: requirement?.contractId ?? 0,
      blocksValue: null,
      requirementId,
      gigValue: '-',
      networkValue: '',
      isWarningNetwork: false,
      isWarningGig: false,
      isValuePublished: false,
      networkDeadline: null,
      gigDeadline: null,
    };

  const blocksInHours = blocks / 2;
  const unmetInHours = unmet / 2;
  const networkInHours = networkBlocks / 2;
  return {
    contractId: requirement.contractId,
    blocksValue: `${blocksInHours}`,
    requirementId,
    gigValue: `${unmetInHours + blocksInHours}`,
    networkValue: `${networkInHours}`,
    isWarningGig: requirement.typeName === RequirementType.max ? unmetInHours > 0 : unmetInHours < 0,
    isWarningNetwork:
      requirement.typeName === RequirementType.max ? networkInHours > blocksInHours : networkInHours < blocksInHours,
    isValuePublished: requirement.publishedThroughBlock > requirement.weekStartBlock,
    networkDeadline: requirement.weekStartBlock - requirement.offset * 2,
    gigDeadline: requirement.weekEndBlock + 1,
  };
};

const _validateComplianceCells = (value: number | undefined): IValueWarningPair => ({
  value: `${value ?? '-'}`,
  isWarning: value ? (Number.isInteger(value) ? value > 0 : false) : false,
});

const _checkIfReasonRequired = (data: IGigComplianceRow) => {
  const keysToValidate = <Array<keyof typeof data>>[
    'reqMin',
    'reqBase',
    'reqPeak',
    'noShows',
    'lateLogin10Minus',
    'lateLogin10Plus',
    'lateAvg',
    'earlyLogout10Minus',
    'earlyLogout10Plus',
    'earlyAvg',
  ];

  return keysToValidate.some(key => {
    if (!data[key]) return;
    if (Array.isArray(data[key]))
      return (data[key] as IRequirementValue[]).some(item => item.isWarningGig || item.isWarningNetwork);
    const item = data[key] as IValueWarningPair;
    return item.isWarning;
  });
};

const _getReasonByWeekId = (data: IGetAdherenceReason[], weekId: number): { reasonTxt: string; reasonNote: string } => {
  const maybeReason = data.find(item => item.weekId === weekId);
  return {
    reasonTxt: maybeReason?.reasonTxt || '',
    reasonNote: maybeReason?.reasonNote || '',
  };
};

const _resTermIdExists = (resTermId: number | undefined | null) => !!resTermId || resTermId === 0;

// This helper doesn't check if the current was removed due to end date
const getContractStatusCode = (contract: IContractRaw, contractPending?: IResignationPending): ContractStatus => {
  const { contractAssnEndBlock, contractEndBlock, resTermId, resTermTypeLabel } = contract;

  // contract with resignation pending
  if (contractPending) {
    // current contract in resign pending state
    return ContractStatus.resignationPending;
  }

  // current contracts, no actions pending
  if (contractAssnEndBlock === 0 && contractEndBlock === null && resTermId === null) {
    return ContractStatus.current;
  }

  // contracts with some actions pending
  if (_resTermIdExists(resTermId)) {
    switch (resTermTypeLabel) {
      case ResTermType.suspension:
        return ContractStatus.suspension;
      case ResTermType.termination:
        return ContractStatus.termination;
    }
  }

  // contract with resignation approved
  if (!contractPending && _resTermIdExists(resTermId)) {
    // already a past resignation approved
    return ContractStatus.resignationApproved;
  }

  return ContractStatus.expired;
};

const getContractStatusLabel = (statusCode: ContractStatus, resTermProcessedOnAsString: string): string => {
  switch (statusCode) {
    case ContractStatus.current:
      return i18n.t`myGigs.gigStatuses.ongoing`;
    case ContractStatus.resignationPending:
      return i18n.t`myGigs.gigStatuses.resignationPending`;
    case ContractStatus.resignationApproved:
      return i18n.t('myGigs.gigStatuses.resignationApproved', {
        date: resTermProcessedOnAsString,
      });
    case ContractStatus.suspension:
      return i18n.t`myGigs.gigStatuses.suspension`;
    case ContractStatus.termination:
      return i18n.t`myGigs.gigStatuses.termination`;
    case ContractStatus.expired:
      return i18n.t`myGigs.gigStatuses.expired`;
    case ContractStatus.unknown:
    default:
      return '-';
  }
};

const transformResTermTypeLabelFromPayload = (label: string): ContractStatus => {
  switch (label) {
    case ResTermType.suspension:
      return ContractStatus.suspension;
    case ResTermType.termination:
      return ContractStatus.suspension;
    default:
      return ContractStatus.unknown;
  }
};

const getResignSurveyQuestionFromContractsList = (questionId: number, resignSurvey: IResignSurveyRaw[]): string => {
  return resignSurvey.find(question => question.questionId === questionId)?.answer || '';
};

const formatResignSurveyQuestions = (data: IResignContractStepTwo): { questionId: number; answer: string }[] =>
  Object.entries(data).map(([key, value]: [key: string, value: string]) => ({
    questionId: Number(key),
    answer: value,
  }));

const formatEffectiveBlock = (effectiveDate: string, effectiveType: EffectiveType): number => {
  // these dates must be UTC
  if (effectiveType === EffectiveType.immediately) {
    return getBlockFromDate(new Date()); // return current block
  } else {
    return getBlockFromDate(addMinutes(startOfDay(new Date(effectiveDate)), 1)); // return first block of the following day
  }
};

const calculateDeadlineInAdvance = (offset: number, weekId: number): Date =>
  subMinutes(subHours(getDateFromWeekId(weekId), offset), 1);

const calculateDeadlineInFinal = (weekId: number): Date => endOfWeek(getDateFromWeekId(weekId));

/**
 * The newest data should be on the beginning of the list sorted by this function
 * It sorts descending by effectiveBlock -> processedTimestamp -> contractAssnStart -> restTermType
 */
const sortAgents = (agent1: IAgentRaw, agent2: IAgentRaw): number => {
  if (agent1.effectiveBlock && agent2.effectiveBlock && agent1.effectiveBlock !== agent2.effectiveBlock) {
    return agent2.effectiveBlock - agent1.effectiveBlock;
  }

  if (agent1.processedTimestamp && agent2.processedTimestamp && agent1.processedTimestamp !== agent2.processedTimestamp)
    return new Date(agent2.processedTimestamp).getTime() - new Date(agent1.processedTimestamp).getTime();

  if (agent1.contractAssnStartBlock !== agent2.contractAssnStartBlock) {
    return agent2.contractAssnStartBlock - agent1.contractAssnStartBlock;
  }

  return (agent2?.resTermTypeId ?? 0) - (agent1?.resTermTypeId ?? 0);
};

const isBeforeDeadlineAdvance = (contract: IUnmetContractRequirement): boolean => {
  return new DateTime().lessEqual(new DateTime(contract.deadlineAdvance));
};

const getClosestDeadlineFormatted = (contract: IUnmetContractRequirement): string =>
  isBeforeDeadlineAdvance(contract) ? contract.deadlineAdvanceFormatted : contract.deadlineFinalFormatted;

const getClosestDeadline = (contract: IUnmetContractRequirement): string =>
  isBeforeDeadlineAdvance(contract) ? contract.deadlineAdvance : contract.deadlineFinal;

/**
 * Sorts contracts by deadlines: from closest deadlines to the most distant
 */
const sortUnmetContractRequirementsByDeadline = (
  contractA: IUnmetContractRequirement,
  contractB: IUnmetContractRequirement,
): number => {
  const deadlineToCompareA = getClosestDeadline(contractA);
  const deadlineToCompareB = getClosestDeadline(contractB);

  return getTimestampFromDateString(deadlineToCompareA) - getTimestampFromDateString(deadlineToCompareB);
};

const categorizeAgents = (agents: IAgent[]): IAgentsCategorized => {
  return agents.sort(sortAgents).reduce<IAgentsCategorized>((acc, agent) => {
    if (!agent.resTermStatus) return acc;
    if (agent.agentStatuses.includes('active') && !isAgentAlreadyAddedToAccumulatedList(agent, acc)) {
      acc.active.push(agent);
      return acc;
    }
    if (agent.agentStatuses.includes('resigned') && !isAgentAlreadyAddedToAccumulatedList(agent, acc)) {
      acc.resignation.push(agent);
      return acc;
    }
    if (agent.agentStatuses.includes('suspended') && !isAgentAlreadyAddedToAccumulatedList(agent, acc)) {
      acc.suspension.push(agent);
      return acc;
    }
    if (agent.agentStatuses.includes('removed') && !isAgentAlreadyAddedToAccumulatedList(agent, acc)) {
      acc.termination.push(agent);
      return acc;
    }
    if (agent.agentStatuses.includes('ended') && !isAgentAlreadyAddedToAccumulatedList(agent, acc)) {
      acc.ended.push(agent);
      return acc;
    }
    return acc;
  }, baseCategorizedAgents());
};

const isAgentAlreadyAddedToAccumulatedList = (agent: IAgent, accList: IAgentsCategorized): boolean => {
  if (!accList) return false;

  if (accList.active && accList.active.find(a => a.userId === agent.userId)) return true;

  if (accList.resignation && accList.resignation.find(a => a.userId === agent.userId)) return true;

  if (accList.suspension && accList.suspension.find(a => a.userId === agent.userId)) return true;

  if (accList.termination && accList.termination.find(a => a.userId === agent.userId)) return true;

  if (accList.ended && accList.ended.find(a => a.userId === agent.userId)) return true;

  return false;
};

const findUnmetRequirementsByReqType = (
  reqLabel: string,
  unmetRequirements?: IUnmetContractRequirement[],
): IUnmetContractRequirement | undefined => unmetRequirements?.find(item => item.requirementLabel === reqLabel);

const transformContractAttendanceResponse = (data: IGetContractAttendanceResponse[][]): IGetContractAttendance[] => {
  return data.flat().map(item => ({
    userId: item.user_id,
    contractId: item.contract_id,
    weekId: item.weekid,
    tzId: item.tzid,
    weekStartBlock: item.w_startblock,
    weekEndBlock: item.w_endblock,
    noShow: item.noshow,
    lateLogin5: item.latelogin5,
    lateLogin5To10: item.latelogin5to10,
    lateLogin10Plus: item.latelogin10plus,
    lateLogin5Secs: item.latelogin5secs,
    lateLogin5To10Secs: item.latelogin5to10secs,
    lateLogin10PlusSecs: item.latelogin10plussecs,
    lateLoginTotal: item.latelogintotal,
    lateLoginTotalSecs: item.latelogintotalsecs,
    earlyLogout5: item.earlylogout5,
    earlyLogout5To10: item.earlylogout5to10,
    earlyLogout10Plus: item.earlylogout10plus,
    earlyLogout5Secs: item.earlylogout5secs,
    earlyLogout5To10Secs: item.earlylogout5to10secs,
    earlyLogout10PlusSecs: item.earlylogout10plussecs,
    earlyLogoutTotal: item.earlylogouttotal,
    earlyLogoutTotalSecs: item.earlylogouttotalsecs,
    lateLoginAvgSeconds: item.lateloginavgseconds,
    earlyLogoutAvgSeconds: item.earlylogoutavgseconds,
  }));
};

const isValidDeviationCell = (value: number, displayInMinutes = false): boolean => {
  const MINUTES_TO_IGNORE = 5;
  return !(isNaN(value) || value === 0 || (displayInMinutes && secondsToMinutes(value) < MINUTES_TO_IGNORE));
};

export {
  calculateUnmetBlocks,
  calculateContractState,
  getWorkSchedules,
  categorizeAgents,
  getAvailabilityItems,
  getHourFromOffsetAndWeekId,
  getWeekdayFromOffsetAndWeekId,
  getWeekdayShortNameFromOffsetAndWeekId,
  prepareGigComplianceToDisplay,
  getOffsetData,
  getContractStatusCode,
  getContractStatusLabel,
  transformResTermTypeLabelFromPayload,
  getResignSurveyQuestionFromContractsList,
  formatResignSurveyQuestions,
  formatEffectiveBlock,
  calculateDeadlineInAdvance,
  calculateDeadlineInFinal,
  findUnmetRequirementsByReqType,
  initialParsedItem,
  sortAgents,
  sortUnmetContractRequirementsByDeadline,
  isBeforeDeadlineAdvance,
  getClosestDeadline,
  getClosestDeadlineFormatted,
  transformContractAttendanceResponse,
  isValidDeviationCell,
  isBlockCoveredByDst,
};
